import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useLang } from "../lib/useLang";
import { useAuth } from "../state/AuthContext";
import { subscribeOrder } from "../lib/orderTrack";
import { getRecentOrders } from "../lib/recentOrders";
import { demoStore } from "../lib/demoStore";
import { deliveryEta } from "../lib/delivery";
import { formatKWD } from "../lib/format";
import { formatSchedule } from "../lib/schedule";
import { modifierSummary } from "../data/modifiers";
import { BRANCHES_SEED } from "../data/branches";
import type { Order } from "../lib/types";

interface OrderSummary {
  id: string;
  createdAt: number;
  total: number;
  orderStatus: string;
}

/** This visitor's orders: device-local (guest) + recorded account history (member),
 *  matched by account uid or phone, newest first. */
function resolveMyOrders(user?: { uid: string; phone: string }): OrderSummary[] {
  const map = new Map<string, OrderSummary>();
  for (const r of getRecentOrders()) {
    const o = demoStore.getOrder(r.id);
    map.set(r.id, {
      id: r.id,
      createdAt: o?.createdAt ?? r.createdAt,
      total: o?.total ?? r.total,
      orderStatus: o?.orderStatus ?? "new",
    });
  }
  if (user) {
    for (const o of demoStore.getOrders()) {
      if (o.customerUid === user.uid || o.customer.phone === user.phone) {
        map.set(o.id, {
          id: o.id,
          createdAt: o.createdAt,
          total: o.total || o.subtotal,
          orderStatus: o.orderStatus,
        });
      }
    }
  }
  return [...map.values()].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

const STEPS = [
  { key: "received", icon: "📝" },
  { key: "preparing", icon: "👨‍🍳" },
  { key: "onway", icon: "🛵" },
  { key: "delivered", icon: "✅" },
] as const;

const STEP_OF: Record<string, number> = {
  new: 0,
  confirmed: 0,
  preparing: 1,
  out_for_delivery: 2,
  delivered: 3,
};

export default function OrderTrackPage() {
  const { t, lang, pick } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState("");
  const [order, setOrder] = useState<Order | null | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    setOrder(undefined);
    return subscribeOrder(id, setOrder);
  }, [id]);

  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const showList = searchParams.has("all");
  const mine = useMemo(
    () => resolveMyOrders(user ? { uid: user.uid, phone: user.phone } : undefined),
    [user],
  );

  // Automatic tracking: if you have an order and didn't ask for the list,
  // jump straight to your latest one (works for guests and signed-in members).
  useEffect(() => {
    if (!id && !showList && mine.length > 0) {
      navigate(`/track/${mine[0].id}`, { replace: true });
    }
  }, [id, showList, mine, navigate]);

  // --- No id: lookup form + this visitor's orders ---
  if (!id) {
    const go = () => {
      const clean = entry.trim().replace(/^#/, "").toUpperCase();
      if (clean) navigate(`/track/${clean}`);
    };
    return (
      <div className="mx-auto max-w-md space-y-5">
        <div>
          <h1 className="display text-2xl text-ink">{t("track.title")}</h1>
          <p className="mt-1 text-sm text-black/55">{t("track.subtitle")}</p>
        </div>
        <div className="card space-y-3 p-4">
          <input
            className="input"
            placeholder={t("track.enterId")}
            value={entry}
            dir="ltr"
            onChange={(e) => setEntry(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && go()}
          />
          <button className="btn-primary w-full" onClick={go} disabled={!entry.trim()}>
            {t("track.find")}
          </button>
        </div>

        {mine.length > 0 && (
          <div>
            <h2 className="display mb-2 text-lg text-ink">{t("track.recent")}</h2>
            <div className="space-y-2">
              {mine.map((r) => (
                <Link
                  key={r.id}
                  to={`/track/${r.id}`}
                  className="card flex items-center gap-2 p-3 transition hover:shadow-raised"
                >
                  <span className="font-bold text-brand" dir="ltr">
                    #{r.id}
                  </span>
                  <span className="chip bg-brand/10 text-brand">
                    {t(`admin.status.${r.orderStatus}`)}
                  </span>
                  <span className="ms-auto text-xs text-black/45">
                    {new Date(r.createdAt).toLocaleDateString(
                      lang === "ar" ? "ar-KW" : "en-GB",
                    )}
                  </span>
                  <span className="font-semibold">{formatKWD(r.total, lang)}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (order === undefined) {
    return <p className="mx-auto max-w-md text-black/50">{t("common.loading")}</p>;
  }
  if (order === null) {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className="display text-2xl text-ink">{t("track.title")}</h1>
        <p className="card p-4 text-sm text-brand">{t("track.notFound")}</p>
        <button className="btn-outline" onClick={() => navigate("/track?all=1")}>
          {t("track.find")}
        </button>
      </div>
    );
  }

  const current = STEP_OF[order.orderStatus] ?? 0;
  const cancelled = order.orderStatus === "cancelled";
  const branch = BRANCHES_SEED.find((b) => b.id === order.branchId);
  const eta = deliveryEta(order.lines);

  return (
    <div className="mx-auto max-w-md space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="display text-2xl text-ink">{t("track.title")}</h1>
        <span className="font-extrabold text-brand" dir="ltr">
          #{order.id}
        </span>
      </div>
      {mine.length > 1 && (
        <Link to="/track?all=1" className="inline-block text-sm font-semibold text-brand">
          🧾 {t("account.orders")}
        </Link>
      )}

      {cancelled ? (
        <p className="card p-4 text-center font-semibold text-brand">
          {t("track.cancelled")}
        </p>
      ) : (
        <div className="card p-5">
          {order.orderStatus !== "delivered" && (
            <div className="mb-4 rounded-xl bg-cream p-3 text-center">
              <div className="text-xs text-black/50">{t("track.eta")}</div>
              <div className="display text-xl text-brand">
                ~{eta.minutes} {t("common.minutes")}
              </div>
            </div>
          )}
          <ol className="relative">
            {STEPS.map((s, i) => {
              const done = i < current;
              const active = i === current;
              return (
                <li key={s.key} className="flex gap-3 pb-6 last:pb-0">
                  <div className="relative flex flex-col items-center">
                    <div
                      className={`grid size-9 place-items-center rounded-full text-base transition ${
                        done || active
                          ? "bg-brand text-white"
                          : "bg-black/[.06] text-black/30"
                      } ${active ? "ring-4 ring-brand/20" : ""}`}
                    >
                      {done ? "✓" : s.icon}
                    </div>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`w-0.5 flex-1 ${done ? "bg-brand" : "bg-black/10"}`}
                        style={{ minHeight: 24 }}
                      />
                    )}
                  </div>
                  <div className="pt-1.5">
                    <div
                      className={`font-semibold ${
                        active ? "text-brand" : done ? "text-ink" : "text-black/40"
                      }`}
                    >
                      {t(`track.${s.key}`)}
                    </div>
                    {active && (
                      <div className="text-xs text-black/45">{t("track.subtitle")}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Order summary */}
      <div className="card p-4">
        <ul className="space-y-1 text-sm">
          {order.lines.map((l, i) => {
            const mods = modifierSummary(l.modifiers, lang);
            return (
              <li key={l.uid ?? `${l.itemId}-${i}`} className="flex justify-between gap-2">
                <span className="min-w-0">
                  {l.qty}× {pick(l.name, l.nameAr)}
                  {(mods || l.notes) && (
                    <span className="block text-xs text-black/40">
                      {[mods, l.notes].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-black/50">{formatKWD(l.price * l.qty, lang)}</span>
              </li>
            );
          })}
        </ul>
        <div className="mt-2 space-y-0.5 border-t border-black/5 pt-2 text-sm">
          {order.scheduledFor != null && (
            <div className="mb-1 flex justify-between font-semibold text-brand">
              <span>🗓️ {t("schedule.title")}</span>
              <span>{formatSchedule(order.scheduledFor, lang)}</span>
            </div>
          )}
          <div className="flex justify-between text-black/55">
            <span>{t("cart.subtotal")}</span>
            <span>{formatKWD(order.subtotal, lang)}</span>
          </div>
          {order.discount != null && order.discount > 0 && (
            <div className="flex justify-between text-emerald-600">
              <span>{t("cart.discount")}</span>
              <span>− {formatKWD(order.discount, lang)}</span>
            </div>
          )}
          <div className="flex justify-between text-black/55">
            <span>
              {order.fulfillmentType === "pickup" ? t("location.pickup") : t("cart.deliveryFee")}
            </span>
            <span>{formatKWD(order.deliveryFee, lang)}</span>
          </div>
          <div className="flex justify-between pt-1 font-bold">
            <span>{t("cart.total")}</span>
            <span className="text-brand">{formatKWD(order.total || order.subtotal, lang)}</span>
          </div>
        </div>
      </div>

      {branch && (
        <div className="card flex items-center justify-between p-4">
          <span className="text-sm text-black/55">{t("track.help")}</span>
          <div className="flex gap-2">
            <a href={`tel:${branch.phone}`} className="chip bg-brand/10 text-brand">
              📞 {t("footer.call")}
            </a>
            <a
              href={`https://wa.me/${branch.whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="chip bg-accent/15 text-accent-dark"
            >
              💬 {t("home.whatsapp")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
